import React, { useEffect, useState, useCallback } from 'react'
import styles from './index.module.scss'
import Modal from '../Modal'
import CloseIcon from '../../assets/icon/CloseIcon'
import CheckboxActiveIcon from '../../assets/icon/CheckboxActiveIcon'
import CheckboxIcon from '../../assets/icon/CheckboxIcon'
import TrashIcon from '../../assets/icon/TrashIcon'
import WorkspaceMembersService from '../../services/workspace-members.service'
import ProjectService from '../../services/project.service'
import { useWorkspace } from '../../context/workspace.context'
import { useUser } from 'cor-business'
import { extractList, getWorkspaceMemberUserId, sortUsersBySelection } from '../../utils/members'
import Radio from '../Radio'

interface ShareProjectModalProps {
    isOpen: boolean
    onClose: () => void
    project: { id: number; name: string; color?: string; is_public?: boolean } | null
    onSaved?: (project: { id: number; is_public: boolean }) => void
}

interface UserOption {
    id: string
    email: string
    first_name: string
    last_name: string
    avatar?: string
}

interface ProjectMember {
    // workspace-member id
    id: number
    userId: string
    name: string
    email: string
    photo?: string
    projectMemberId?: number
    isSelf?: boolean
}

const ChevronDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6L8 10L12 6" stroke="#64617E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const getUserDisplayName = (user: UserOption) => {
    if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim()
    }

    return user.email
}

const getPeopleLabel = (count: number) => {
    const mod10 = count % 10
    const mod100 = count % 100

    if (mod10 === 1 && mod100 !== 11) return 'человек'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'человека'
    return 'человек'
}

const getInitials = (name: string, email = '') => {
    const initials = name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)

    return initials || email[0]?.toUpperCase() || 'U'
}

const toUserOption = (member: any, photoMap: Map<string, string>): UserOption | null => {
    const userId = getWorkspaceMemberUserId(member)

    if (!userId) {
        return null
    }

    return {
        id: userId,
        email: member.email || member.user?.email || '',
        first_name: member.first_name || member.user?.first_name || '',
        last_name: member.last_name || member.user?.last_name || '',
        avatar: photoMap.get(userId) || '',
    }
}

export default function ShareProjectModal({ isOpen, onClose, project, onSaved }: ShareProjectModalProps) {
    const { workspace } = useWorkspace()
    const { user } = useUser()
    const [members, setMembers] = useState<ProjectMember[]>([])
    const [membersOpen, setMembersOpen] = useState(false)
    const [memberPickerOpen, setMemberPickerOpen] = useState(false)
    const [isPrivate, setIsPrivate] = useState(false)
    const [removedUserIds, setRemovedUserIds] = useState<Set<string>>(new Set())
    const [disablePrivacyConfirmOpen, setDisablePrivacyConfirmOpen] = useState(false)

    const [users, setUsers] = useState<UserOption[]>([])
    const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([])
    const [search, setSearch] = useState('')
    const [searchFocused, setSearchFocused] = useState(false)
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [usersLoaded, setUsersLoaded] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const currentUserId = String(user?.user_id ?? user?.id ?? '')

    const loadMembers = useCallback(async () => {
        if (!workspace?.id || !project) return

        try {
            const [membersData, photosData, projectMembersData] = await Promise.all([
                WorkspaceMembersService.getMembers(Number(workspace.id)),
                WorkspaceMembersService.getMemberPhotos(Number(workspace.id)).catch(() => null),
                WorkspaceMembersService.getProjectMembers(Number(project.id)).catch(() => null),
            ])
            const all: any[] = extractList(membersData)
            const photoItems: any[] = extractList(photosData)
            const photoMap = new Map<string, string>()
            photoItems.forEach((p: any) => {
                if (p.user_id && p.photo_data_url) photoMap.set(p.user_id, p.photo_data_url)
            })

            setUsers(all.map((member) => toUserOption(member, photoMap)).filter(Boolean) as UserOption[])
            setUsersLoaded(true)

            const projectMembers = extractList(projectMembersData)
            const wsByUser = new Map<string, any>()
            all.forEach((m) => wsByUser.set(getWorkspaceMemberUserId(m), m))

            const inProject = projectMembers.map((pm: any) => {
                const userId = String(pm.user_id ?? pm.userId ?? pm.user?.user_id ?? pm.user?.id ?? '')
                const ws = wsByUser.get(userId) || {}
                const firstName = ws.first_name || ws.user?.first_name || pm.first_name || ''
                const lastName = ws.last_name || ws.user?.last_name || pm.last_name || ''
                const name = `${firstName} ${lastName}`.trim() || ws.email || pm.email || userId
                return {
                    id: ws.id ?? pm.id,
                    userId,
                    name,
                    email: ws.email || ws.user?.email || pm.email || '',
                    photo: photoMap.get(userId) || '',
                    projectMemberId: pm.id ?? pm.project_member_id ?? pm.member_id,
                    isSelf: currentUserId !== '' && userId === currentUserId,
                }
            }).filter((member) => member.userId)

            setMembers(inProject)
            setRemovedUserIds(new Set())
        } catch (e) {
            console.error('Failed to load project members:', e)
        }
    }, [workspace?.id, project, currentUserId])

    useEffect(() => {
        if (!isOpen) return
        setIsPrivate(project?.is_public === false)
        void loadMembers()
    }, [isOpen, loadMembers])

    const resetState = () => {
        setUsers([])
        setSelectedUsers([])
        setSearch('')
        setSearchFocused(false)
        setUsersLoaded(false)
        setMemberPickerOpen(false)
        setMembersOpen(false)
        setRemovedUserIds(new Set())
        setDisablePrivacyConfirmOpen(false)
        setError(null)
    }

    const handleClose = () => {
        onClose()
        resetState()
    }

    const fetchUsers = async (query = '') => {
        if (!workspace?.id) return

        setLoadingUsers(true)
        try {
            const [membersData, photosData] = await Promise.all([
                WorkspaceMembersService.getMembers(Number(workspace.id)),
                WorkspaceMembersService.getMemberPhotos(Number(workspace.id)).catch(() => null),
            ])
            const photoItems: any[] = extractList(photosData)
            const photoMap = new Map<string, string>()
            photoItems.forEach((p: any) => {
                if (p.user_id && p.photo_data_url) photoMap.set(p.user_id, p.photo_data_url)
            })

            setUsers(extractList(membersData).map((member) => toUserOption(member, photoMap)).filter(Boolean) as UserOption[])
            setUsersLoaded(true)
        } catch (e) {
            console.error('Failed to fetch users:', e)
            setUsers([])
        } finally {
            setLoadingUsers(false)
        }
    }

    const handleSearchFocus = () => {
        setSearchFocused(true)
        if (!usersLoaded) void fetchUsers('')
    }

    const handleSearchChange = (value: string) => {
        setSearch(value)
        if (!usersLoaded && !loadingUsers) void fetchUsers(value)
    }

    const toggleUser = (user: UserOption) => {
        setSelectedUsers((prev) => (
            prev.some((item) => item.id === user.id)
                ? prev.filter((item) => item.id !== user.id)
                : [...prev, user]
        ))
    }

    const existingUserIds = new Set(members.map((m) => String(m.userId)))
    const filteredUsers = sortUsersBySelection(
        users.filter((user) => {
            if (!search) return true

            const name = getUserDisplayName(user).toLowerCase()
            const query = search.toLowerCase()
            return name.includes(query) || user.email?.toLowerCase().includes(query)
        }),
        members.map((member) => member.userId),
    )

    const showUsersDropdown = searchFocused && (filteredUsers.length > 0 || loadingUsers)

    const persistSettings = async () => {
        if (!project || submitting) return

        setSubmitting(true)
        setError(null)

        const isPublic = !isPrivate
        // Only send the privacy flag when it actually changed, so opening the
        // modal just to add someone does not rewrite the project.
        // `is_public !== false` mirrors how the toggle was initialised, so an
        // absent flag is read as public by both and never counts as a change.
        const privacyChanged = (project.is_public !== false) !== isPublic
        // Each group is reported separately: a failed membership change must not
        // be described as a failed privacy change (and vice versa), and the
        // privacy flag may well have been applied even when members did not.
        const [privacyResult, ...memberResults] = await Promise.allSettled([
            privacyChanged
                ? ProjectService.update(String(project.id), { is_public: isPublic })
                : Promise.resolve(undefined),
            ...Array.from(removedUserIds).map((userId) => (
                WorkspaceMembersService.removeProjectMember(Number(project.id), userId)
            )),
            ...selectedUsers.map((user) => WorkspaceMembersService.addProjectMember({
                user_id: user.id,
                project_id: Number(project.id),
                role_in_project: 'MEMBER',
            })),
        ])

        const privacyFailed = privacyResult.status === 'rejected'
        const failedMembers = memberResults.filter((result) => result.status === 'rejected') as PromiseRejectedResult[]

        if (!privacyFailed) {
            onSaved?.({ id: project.id, is_public: isPublic })
        }

        if (privacyFailed || failedMembers.length) {
            const reason = (privacyFailed ? privacyResult.reason : failedMembers[0]?.reason) as any
            const detail = reason?.response?.data?.detail || reason?.data?.detail

            console.error('Failed to save project privacy settings:', privacyResult, ...failedMembers)
            setError(detail || (
                privacyFailed && failedMembers.length
                    ? 'Не удалось сохранить настройки приватности и изменить участников'
                    : privacyFailed
                        ? 'Не удалось сохранить настройки приватности'
                        : 'Настройки приватности сохранены, но изменить участников не удалось'
            ))
            // Membership changes partly went through, so the pending removals and
            // additions no longer describe the server state — reload the real one.
            setSelectedUsers([])
            setSearch('')
            await loadMembers()
            setSubmitting(false)
            return
        }

        setSubmitting(false)
        handleClose()
    }

    const handleSave = () => {
        // Compare against what the toggle started at, so this stays a "privacy
        // was turned off" check rather than a "project happened to be private"
        // one — the latter reads stale once a save leaves the modal open.
        const wasPrivate = project?.is_public === false
        const privacyIsBeingDisabled = wasPrivate && !isPrivate

        if (privacyIsBeingDisabled) {
            setError(null)
            setDisablePrivacyConfirmOpen(true)
            return
        }

        void persistSettings()
    }

    const cancelDisablePrivacy = () => {
        setDisablePrivacyConfirmOpen(false)
        setIsPrivate(true)
        setError(null)
    }

    const handleRemoveMember = (member: ProjectMember) => {
        if (member.isSelf) return

        setRemovedUserIds((currentIds) => new Set(currentIds).add(String(member.userId)))
    }

    const visibleMembers = members.filter((member) => !removedUserIds.has(String(member.userId)))
    const removableMembers = visibleMembers.filter((m) => !m.isSelf)
    const previewMembers = [
        ...visibleMembers.map((member) => ({
            id: member.userId,
            name: member.name,
            email: member.email,
            photo: member.photo,
        })),
        ...selectedUsers.map((selectedUser) => ({
            id: selectedUser.id,
            name: getUserDisplayName(selectedUser),
            email: selectedUser.email,
            photo: selectedUser.avatar,
        })),
    ]

    const handleRemoveAll = () => {
        if (removableMembers.length === 0) return

        setRemovedUserIds((currentIds) => {
            const nextIds = new Set(currentIds)
            removableMembers.forEach((member) => nextIds.add(String(member.userId)))
            return nextIds
        })
    }

    return (
        <>
        <Modal isOpen={isOpen && !disablePrivacyConfirmOpen} onRequestClose={handleClose} width={500}>
            <div className={styles.share}>
                <button type="button" className={styles.share__close} onClick={handleClose} aria-label="Закрыть">
                    <CloseIcon />
                </button>

                <h3 className={styles.share__title}>Настроить приватность</h3>

                <div className={styles.share__privacyRow}>
                    <span className={styles.share__privacyLabel}>Сделать приватно</span>
                    <Radio isOn={isPrivate} changeState={setIsPrivate} />
                </div>
                <p className={styles.share__privacyDescription}>
                    При активном состоянии — только вы и приглашённые участники будут иметь доступ к проекту. При неактивном — проект будет виден всем
                </p>

                <div className={styles.share__peopleRow}>
                    <span className={styles.share__peopleLabel}>Поделиться с:</span>
                    <div className={styles.share__peopleControls}>
                        <div className={styles.share__avatarStack}>
                            {previewMembers.slice(0, 3).map((member) => (
                                member.photo ? (
                                    <img key={member.id} className={styles.share__stackAvatar} src={member.photo} alt={member.name} />
                                ) : (
                                    <span key={member.id} className={styles.share__stackAvatarPlaceholder}>
                                        {getInitials(member.name, member.email)}
                                    </span>
                                )
                            ))}
                            {previewMembers.length > 3 && (
                                <span className={`${styles.share__stackAvatarPlaceholder} ${styles.share__stackCount}`}>
                                    +{previewMembers.length - 3}
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            className={`${styles.share__addMember} ${memberPickerOpen ? styles.share__addMemberActive : ''}`}
                            onClick={() => setMemberPickerOpen((open) => !open)}
                            aria-label="Добавить участников"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                {memberPickerOpen && (
                    <div className={styles.share__picker}>
                        <div className={`${styles.share__search} ${searchFocused ? styles.share__searchFocused : ''}`}>
                            <svg className={styles.share__searchIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path fillRule="evenodd" clipRule="evenodd" d="M4.99902 11.2922C4.99902 7.8171 7.81612 5 11.2912 5C14.7662 5 17.5833 7.8171 17.5833 11.2922C17.5833 12.7779 17.0685 14.1431 16.2075 15.2196L16.1635 15.2745L18.8148 17.9259C19.0605 18.1716 19.0605 18.57 18.8148 18.8157C18.5691 19.0614 18.1707 19.0614 17.925 18.8157L15.2736 16.1644L15.2187 16.2084C14.1422 17.0695 12.7769 17.5843 11.2912 17.5843C7.81612 17.5843 4.99902 14.7672 4.99902 11.2922ZM11.2912 6.25843C8.51113 6.25843 6.25746 8.51211 6.25746 11.2922C6.25746 14.0722 8.51113 16.3259 11.2912 16.3259C14.0712 16.3259 16.3249 14.0722 16.3249 11.2922C16.3249 8.51211 14.0712 6.25843 11.2912 6.25843Z" fill="currentColor"/>
                            </svg>
                            <input
                                className={styles.share__searchInput}
                                placeholder="Имя, фамилия или email"
                                value={search}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onFocus={handleSearchFocus}
                                onBlur={() => window.setTimeout(() => setSearchFocused(false), 150)}
                            />
                            {showUsersDropdown && (
                                <div className={styles.share__dropdown}>
                                    {loadingUsers ? (
                                        <div className={`${styles.share__dropdownItem} ${styles.share__dropdownItemMuted}`}>Загрузка...</div>
                                    ) : filteredUsers.length === 0 ? (
                                        <div className={`${styles.share__dropdownItem} ${styles.share__dropdownItemMuted}`}>Никого не найдено</div>
                                    ) : filteredUsers.map((user) => {
                                        const isAlreadyMember = existingUserIds.has(String(user.id))
                                        return (
                                            <div
                                                key={user.id}
                                                className={`${styles.share__dropdownItem} ${isAlreadyMember ? styles.share__dropdownItemMember : ''}`}
                                                title={isAlreadyMember ? 'Уже в проекте' : undefined}
                                                onMouseDown={(event) => {
                                                    event.preventDefault()
                                                    if (!isAlreadyMember) toggleUser(user)
                                                }}
                                            >
                                                <div className={styles.share__dropdownItemLeft}>
                                                    {user.avatar ? (
                                                        <img className={styles.share__avatar} src={user.avatar} alt={getUserDisplayName(user)} />
                                                    ) : (
                                                        <span className={styles.share__avatarPlaceholder}>{getInitials(getUserDisplayName(user), user.email)}</span>
                                                    )}
                                                    <span>{getUserDisplayName(user)}</span>
                                                </div>
                                                {isAlreadyMember || selectedUsers.some((item) => item.id === user.id)
                                                    ? <CheckboxActiveIcon />
                                                    : <CheckboxIcon />}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {selectedUsers.length > 0 && (
                            <div className={styles.share__chips}>
                                {selectedUsers.map((selectedUser) => (
                                    <div key={selectedUser.id} className={styles.share__chip}>
                                        <span>{getUserDisplayName(selectedUser)}</span>
                                        <button type="button" className={styles.share__chipRemove} onClick={() => toggleUser(selectedUser)} aria-label="Убрать участника">
                                            <CloseIcon />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className={styles.share__members}>
                    <button type="button" className={styles.share__membersHeader} onClick={() => setMembersOpen((open) => !open)}>
                        <span className={styles.share__membersTitle}>Участники проекта</span>
                        <span className={`${styles.share__membersChevron} ${membersOpen ? styles.share__membersChevronOpen : ''}`}>
                            <ChevronDownIcon />
                        </span>
                    </button>

                    {membersOpen && (
                        <>
                        <div className={styles.share__membersSubheader}>
                            <span className={styles.share__membersCount}>
                                {visibleMembers.length} {getPeopleLabel(visibleMembers.length)}
                            </span>
                            {removableMembers.length > 0 && (
                                <button
                                    type="button"
                                    className={styles.share__membersRemoveAll}
                                    onClick={handleRemoveAll}
                                >
                                    Удалить всех
                                </button>
                            )}
                        </div>
                        <div className={styles.share__membersList}>
                            {visibleMembers.length === 0 ? (
                                <div className={styles.share__membersEmpty}>В проекте пока нет участников</div>
                            ) : (
                                visibleMembers.map((member) => (
                                    <div key={member.id} className={styles.share__memberRow}>
                                        {member.photo ? (
                                            <img className={styles.share__avatar} src={member.photo} alt={member.name} />
                                        ) : (
                                            <span className={styles.share__avatarPlaceholder}>{getInitials(member.name, member.email)}</span>
                                        )}
                                        <span className={styles.share__memberName}>{member.name}</span>
                                        {!member.isSelf && (
                                            <button type="button" className={styles.share__memberRemove} onClick={() => handleRemoveMember(member)} aria-label="Удалить из проекта">
                                                <TrashIcon />
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                        </>
                    )}
                </div>

                {error && <div className={styles.share__error}>{error}</div>}

                <div className={styles.share__actions}>
                    <button type="button" className={styles.share__cancel} onClick={handleClose} disabled={submitting}>Отменить</button>
                    <button type="button" className={styles.share__submit} onClick={handleSave} disabled={submitting}>
                        {submitting ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            </div>
        </Modal>
        <Modal
            isOpen={isOpen && disablePrivacyConfirmOpen}
            onRequestClose={cancelDisablePrivacy}
            width={420}
        >
            <div className={`${styles.share} ${styles.share__confirmation}`}>
                <button
                    type="button"
                    className={styles.share__close}
                    onClick={cancelDisablePrivacy}
                    aria-label="Закрыть"
                >
                    <CloseIcon />
                </button>

                <h3 className={styles.share__confirmationTitle}>
                    Вы уверены, что хотите выключить приватность?
                </h3>
                <p className={styles.share__confirmationDescription}>
                    В таком случае проект станет виден всем участникам пространства. Если захотите снова настроить приватность, вы можете снова активировать эту функцию и выбрать участников вручную снова
                </p>

                {error && <div className={styles.share__error}>{error}</div>}

                <div className={`${styles.share__actions} ${styles.share__confirmationActions}`}>
                    <button
                        type="button"
                        className={styles.share__cancel}
                        onClick={cancelDisablePrivacy}
                        disabled={submitting}
                    >
                        Отменить действие
                    </button>
                    <button
                        type="button"
                        className={styles.share__submit}
                        onClick={() => void persistSettings()}
                        disabled={submitting}
                    >
                        {submitting ? 'Сохранение...' : 'Выключить приватность'}
                    </button>
                </div>
            </div>
        </Modal>
        </>
    )
}
